<script setup>
  import {ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {useForm, usePage} from "@inertiajs/vue3";
  import {MedButton, MedCheckbox, MedFormField, MedLabel} from "@pharma4u/medicheck-components";
  import {cloneDeep} from "es-toolkit";

  defineOptions({name: "SymptomContextDialog"});

  const pageProperties = ref(null);

  const refContextDialog = ref(null);

  const isLoading = ref(false);
  const optionList = ref(null);
  const form = useForm({
    symptom: null,
  });

  const open = (symptom) => {
    // To prevent the error "Failed to execute 'replaceState' on 'History': #<Object> could not be cloned"
    // we have to convert the initial symptoms reactive object to a plain object. Note: the internal
    // toRaw() or toValue() methods do not work here.
    // This seems to be a valid workaround or an everlasting issue in InertiaJS, see:
    // https://github.com/inertiajs/inertia/issues/775#issuecomment-1003914525
    form.symptom = cloneDeep(symptom);

    pageProperties.value = usePage().props;

    refContextDialog.value.open();

    setupDialog();
  };

  const close = () => {
    if (refContextDialog.value) {
      refContextDialog.value.close();
    }
  };

  const setupDialog = () => {
    const list = [];

    switch (form.symptom.context) {
      case "fam": {
        (pageProperties.value?.medications?.data ?? []).map((medication) => {
          if (medication.abdata_key_fam) {
            list.push({
              name: medication.fam,
              checkbox: form.symptom.contextvalue.includes(medication.abdata_key_fam),
              context: {
                type: "fam",
                mc_context_id: medication.abdata_key_fam,
              },
            });
          }
        });

        break;
      }
      case "diagnosis": {
        (pageProperties.value?.diseases?.data ?? []).map((disease) => {
          list.push({
            name: disease.disease,
            checkbox: form.symptom.contextvalue.includes(disease.abdata_key_miv),
            context: {
              type: "diagnosis",
              mc_context_id: disease.abdata_key_miv,
            },
          });
        });

        break;
      }
      case "laborvalue": {
        (pageProperties.value?.laborvalues?.data ?? []).map((laborValue) => {
          // 182 = body height - 183 = body weight
          if (
            laborValue.mc_laborvalue_key !== 182 &&
            laborValue.mc_laborvalue_key !== 183 &&
            laborValue.deviation !== "normal"
          ) {
            list.push({
              name: laborValue.type,
              checkbox: form.symptom.contextvalue.includes(laborValue.mc_laborvalue_id),
              context: {
                type: "laborvalue",
                mc_context_id: laborValue.mc_laborvalue_id,
              },
            });
          }
        });

        break;
      }
      // No default
    }

    optionList.value = list;
  };

  const closeAndResetDialog = () => {
    const rollbackValues = (pageProperties.value?.symptoms?.data ?? []).find(
      (symptom) => symptom.id === form.symptom.id,
    );

    form.symptom.symptomcontexts = rollbackValues?.symptomcontexts;
    close();
  };

  const save = () => {
    form
      .transform((data) => {
        return data.symptom;
      })
      .put(
        route("symptoms.update", {
          patient: pageProperties.value.patient.id,
          record: pageProperties.value.record.id,
          symptom: form.symptom.id,
        }),
        {
          // preserveState: false,
          preserveScroll: true,
          only: ["flash", "errors", "symptoms"],
          onStart: () => {
            isLoading.value = true;
          },
          onFinish: () => {
            isLoading.value = false;
            close();
          },
        },
      );
  };

  const changeForm = ({context, isChecked}) => {
    let list = Array.isArray(form.symptom.symptomcontexts) ? cloneDeep(form.symptom.symptomcontexts) : [];

    if (isChecked) {
      list.push(context);
    } else {
      list = list.filter((symptomContext) => symptomContext.mc_context_id !== context.mc_context_id);
    }

    form.symptom.symptomcontexts = list;
  };

  defineExpose({
    open,
  });
</script>

<template>
  <ComponentDialog
    ref="refContextDialog"
    dialog-title="Warum? / Was?"
    :is-loading="isLoading"
    :test-id="getTestId('context')"
  >
    <template #content>
      <template v-if="optionList.length === 0">Es wurden keine passenden Optionen gefunden.</template>

      <template v-else>
        <template v-for="(item, index) in optionList" :key="index">
          <div class="flex pt-5 first:pt-1">
            <MedFormField orientation="horizontal">
              <MedCheckbox
                v-model="item.checkbox"
                :data-test="getTestId(`context-dialog-${index}-checkbox`)"
                @update:model-value="changeForm({context: item.context, isChecked: $event})"
              />

              <MedLabel>
                {{ item.name }}
              </MedLabel>
            </MedFormField>
          </div>
        </template>
      </template>
    </template>

    <template #actions>
      <template v-if="optionList.length > 0">
        <MedButton
          variant="primary"
          :disabled="isLoading"
          :data-test="getTestId('context-dialog-save-button')"
          @click="save"
        >
          Speichern
        </MedButton>
      </template>

      <MedButton
        :disabled="isLoading"
        :data-test="getTestId('context-dialog-close-button')"
        @click="closeAndResetDialog"
      >
        Abbrechen
      </MedButton>
    </template>
  </ComponentDialog>
</template>
